import React from 'react'
import { alpha, Box, Stack, SvgIcon, SxProps } from '@mui/material'
import { Typography } from '@mui/material/'
import { OverflowTooltipWrapper } from '../../OverflowTooltipWrapper/OverflowTooltipWrapper'
import { DonutLegendItem } from '../types'
import { Theme } from '@mui/material/styles'

interface ChartVisibilityState {
    [key: string]: boolean
}

interface SimpleLegendProps {
    items: DonutLegendItem[]
    hiddenItems: ChartVisibilityState
    hoveredIndex?: number | null
    pendingIndex?: number | null
    activeIndex?: number | null
    onItemClick?: (index: number) => void
    onItemHover?: (index: number | null) => void
    direction?: 'row' | 'column'
}

export const SimpleLegend = ({
    items,
    hiddenItems,
    hoveredIndex,
    pendingIndex,
    activeIndex,
    onItemClick,
    onItemHover,
    direction = 'row',
}: SimpleLegendProps) => {
    const handleMouseLeave = () => {
        onItemHover?.(null)
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: direction,
                flexWrap: direction === 'row' ? 'wrap' : 'nowrap',
                gap: 2,
                width: '100%',
            }}
            onMouseLeave={handleMouseLeave}
        >
            {items.map((item, index) => {
                const isHidden = !!hiddenItems[item.label]
                const isHovered = hoveredIndex === index
                const isPending = pendingIndex === index
                const isActive = activeIndex === index

                return (
                    <LegendItem
                        key={item.label}
                        item={item}
                        index={index}
                        isHidden={isHidden}
                        isHovered={isHovered}
                        isPending={isPending}
                        isActive={isActive}
                        onClick={onItemClick}
                        onHover={onItemHover}
                        direction={direction}
                    />
                )
            })}
        </Box>
    )
}

interface LegendItemProps {
    item: DonutLegendItem
    index: number
    isHidden: boolean
    isHovered: boolean
    isPending: boolean | undefined
    isActive: boolean | undefined
    onClick?: (index: number) => void
    onHover?: (index: number | null) => void
    direction?: 'row' | 'column'
    sx?: SxProps<Theme>
}

export const LegendItem: React.FC<LegendItemProps> = ({
    item,
    index,
    isHidden,
    isHovered,
    isPending,
    isActive,
    onClick,
    onHover,
    direction = 'row',
    sx,
}) => {
    const handleMouseEnter = () => {
        if (!isHidden) {
            onHover?.(index)
        }
    }

    const handleMouseLeave = () => {
        onHover?.(null)
    }

    return (
        <Box
            onClick={() => onClick?.(index)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onBlur={() => onHover?.(null)}
            tabIndex={0}
            role="button"
            aria-label={`Toggle ${item.label} visibility`}
            aria-pressed={!isHidden}
            data-pending={(!isHidden && isPending) || undefined}
            data-active={(!isHidden && isActive) || undefined}
            sx={[
                {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: direction === 'row' ? 'auto' : '100%',
                    cursor: onClick ? 'pointer' : 'default',
                    opacity: isHidden ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                    borderRadius: 1,
                    padding: '2px 6px',
                    position: 'relative',
                    '&:hover': !!onClick
                        ? {
                              backgroundColor: (theme) => alpha(theme.palette.figma.grayscale.tier1, 0.05),
                              transform: onHover ? `scale(${direction === 'column' ? 1.0125 : 1.05})` : 'unset',
                          }
                        : {},
                    '&:focus-visible': {
                        outline: '2px solid #0078d4',
                        outlineOffset: '2px',
                    },
                    '&[data-pending]::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        height: '2px',
                        backgroundColor: item.color,
                        animation: 'pendingProgress 1s linear',
                    },
                    '&[data-active]::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        width: '100%',
                        height: '2px',
                        backgroundColor: item.color,
                    },
                    '@keyframes pendingProgress': {
                        from: { width: 0 },
                        to: { width: '100%' },
                    },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <SvgIcon
                component={item.icon}
                inheritViewBox
                sx={{
                    color: item.color,
                    fontSize: '12px',
                }}
            />
            <OverflowTooltipWrapper
                placement="top-start"
                arrow
                title={
                    <Stack gap={1} overflow="scroll">
                        <Typography
                            variant="14x400"
                            color="figma.grayscale.textHier1"
                            sx={{
                                whiteSpace: 'pre-line',
                                overflow: 'scroll',
                                maxHeight: '50vh',
                            }}
                        >
                            {item.label}
                        </Typography>
                    </Stack>
                }
                componentsProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: 'figma.grayscale.tier7',
                            px: 2,
                            py: 1,
                            borderRadius: '6px',
                            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                        },
                    },
                    arrow: {
                        sx: {
                            color: 'figma.grayscale.tier7',
                        },
                    },
                }}
            >
                <Typography variant="16x400" color="figma.grayscale.tier3" noWrap>
                    {item.label}
                </Typography>
            </OverflowTooltipWrapper>
        </Box>
    )
}
